// ===== Packages =====

import styled                           from 'styled-components';
import { ReactSVG }                     from 'react-svg';

// ===== Constants =====

import FONT_TYPE                        from '../../constants/fontType';
import {
    CART_AND_CHECKOUT_FONT_SIZE_LARGE,
    CART_AND_CHECKOUT_FONT_SIZE_SMALL,
    BODY_FONT_SIZE,
    DEFAULT_FONT_SIZE,
}                                       from '../../constants/generalConstants';

const ACTION_BUTTON_HEIGHT = 3.125 * BODY_FONT_SIZE;

interface CheckpointFormContainerProps {
    extraPadding?: boolean,
}
export const CheckpointFormContainer = styled.div<CheckpointFormContainerProps>`
    overflow-y: scroll;
    -webkit-overflow-scrolling: touch; /* smooth scrolling for iOS */
    touch-action: pan-y;
    height: 100%;
    width: 100%;
    padding-bottom: ${({ extraPadding }) => `${extraPadding ? 18.75 * BODY_FONT_SIZE : 3.125 * BODY_FONT_SIZE}px`};

    ${({ theme }) => theme.mediaQuery.small} {
        padding: ${`${0.625 * BODY_FONT_SIZE}px`};
        padding-bottom: ${({ extraPadding }) => `${extraPadding ? 18.75 * BODY_FONT_SIZE : 3.125 * BODY_FONT_SIZE}px`};
    }

    ${({ theme }) => theme.mediaQuery.extraSmall} {
        padding: ${`${0.625 * BODY_FONT_SIZE}px`};
        padding-bottom: ${`${3.125 * BODY_FONT_SIZE}px`};
        overflow-y: hidden;
        height: auto;
    }
`;

interface InputGroupContainerProps {
    stackOnMobile?: boolean,
}
export const InputGroupContainer = styled.div<InputGroupContainerProps>`
    display: flex;
    flex-direction: row;
    width: 100%;

    ${({ theme }) => theme.mediaQuery.extraSmall} {
        ${({ stackOnMobile }) => stackOnMobile && `
            flex-direction: column;

            > div {
                margin-left: 0;
                padding-right: 0;
            }
        `}
    }
`;

interface InputContainerProps {
    paddingRight?: boolean,
    paddingLeft?: boolean,
    noMargin?: boolean,
}
export const InputContainer = styled.div<InputContainerProps>`
    padding-right: ${({ paddingRight }) => `${paddingRight ? 0.3125 * BODY_FONT_SIZE : 0}px`};
    margin-left: ${({ paddingLeft }) => `${paddingLeft ? 0.3125 * BODY_FONT_SIZE : 0}px`};
    margin-bottom: ${({ noMargin }) => `${noMargin ? 0 : 0.625 * BODY_FONT_SIZE}px`};
    width: 100%;
`;

interface CheckpointTitleProps {
    noMarginTop?: boolean,
    largeMarginTop?: boolean,
}
export const CheckpointTitle = styled.h2<CheckpointTitleProps>`
    font-family: ${FONT_TYPE.PRESS_START};
    font-weight: 500;
    font-size: ${`${CART_AND_CHECKOUT_FONT_SIZE_LARGE}em`};
    color: ${({ theme }) => theme.verascopeColor.purple100};
    text-transform: uppercase;
    line-height: 2em;
    margin: 0;
    margin-top: ${({ noMarginTop }) => `${noMarginTop ? 0 : 2.5 * BODY_FONT_SIZE}px`};
    margin-bottom: ${`${1.25 * BODY_FONT_SIZE}px`};
    

    ${({ theme }) => theme.mediaQuery.small} {
        margin-top: ${`${1.25 * BODY_FONT_SIZE}px`};
        font-size: ${`${CART_AND_CHECKOUT_FONT_SIZE_SMALL}em`};
    }

    ${({ theme }) => theme.mediaQuery.extraSmall} {
        margin-top: ${({ noMarginTop, largeMarginTop }) => {
        if (noMarginTop) return '0px';
        if (largeMarginTop) return `${3.75 * BODY_FONT_SIZE}px`;
        return `${1.25 * BODY_FONT_SIZE}px`;
    }};
        font-size: ${`${CART_AND_CHECKOUT_FONT_SIZE_SMALL}em`};
        text-align: center;
        line-height: 1.8em;
    }
`;

export const ButtonsContainer = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: space-between;

    ${({ theme }) => theme.mediaQuery.extraSmall} {
        flex-direction: column-reverse;
    }
`;

export const BackButtonIconContainer = styled.div`
    position: relative;
    left: ${`${0.625 * BODY_FONT_SIZE}px`};
    width: ${`${1.875 * BODY_FONT_SIZE}px`};
    height: ${`${1.875 * BODY_FONT_SIZE}px`};
    margin-right: ${`${0.9375 * BODY_FONT_SIZE}px`};
    transform: rotate(180deg);

    & div {
        display: flex;
        align-items: center;
        justify-content: center;
        height: 100%;
        width: 100%;
    }

    & svg {
        width: 100%;
        height: 100%;
    }

    & path, & rect {
        fill: ${({ theme }) => theme.verascopeColor.purple100};
    }
`;

interface BackButtonProps {
    customCursorActive: boolean,
    detectTouchDevice: boolean,
}
export const BackButton = styled.div<BackButtonProps>`
    display: flex;
    flex-direction: row;
    align-items: center;
    width: 100%;
    height: ${`${ACTION_BUTTON_HEIGHT}px`};
    font-family: ${FONT_TYPE.PRESS_START};
    font-weight: 500;
    font-size: ${`${CART_AND_CHECKOUT_FONT_SIZE_LARGE}em`};
    color: ${({ theme }) => theme.verascopeColor.purple100};
    text-transform: uppercase;
    margin-top: ${`${1.25 * BODY_FONT_SIZE}px`};
    margin-right: ${`${2.5 * BODY_FONT_SIZE}px`};
    padding: 0px;
    cursor: ${({ customCursorActive }) => (customCursorActive ? 'default' : 'pointer')};

    &:hover {
        ${({ detectTouchDevice, theme }) => (!detectTouchDevice && `
            color: ${theme.verascopeColor.orange200};
        `)}
    }

    &:hover ${BackButtonIconContainer} {
        left: 0px;

        & path, & rect {
            fill: ${({ theme }) => theme.verascopeColor.orange200};
        }
    }

    ${({ theme }) => theme.mediaQuery.small} {
        font-size: ${`${CART_AND_CHECKOUT_FONT_SIZE_SMALL}em`};
    }

    ${({ theme }) => theme.mediaQuery.extraSmall} {
        font-size: ${`${CART_AND_CHECKOUT_FONT_SIZE_SMALL}em`};
        margin-top: ${`${0.625 * BODY_FONT_SIZE}px`};
        margin-right: 0px;
        justify-content: center;
    }
`;

export const BackButtonTextContainer = styled.h3``;

interface ActionButtonProps {
    noHover: boolean,
    detectTouchDevice: boolean,
    customCursorActive: boolean,
}
export const ActionButton = styled.button<ActionButtonProps>`
    outline: none;
    border: none;
    background: none;
    cursor: ${({ customCursorActive }) => (customCursorActive ? 'none' : 'pointer')};
    padding: 0px;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: ${`${ACTION_BUTTON_HEIGHT}px`};
    font-family: ${FONT_TYPE.PRESS_START};
    font-weight: 500;
    font-size: ${`${CART_AND_CHECKOUT_FONT_SIZE_LARGE + 0.1}em`};
    color: ${({ theme }) => theme.color.white};
    text-transform: uppercase;
    background: ${({ theme }) => theme.verascopeColor.orange200};
    margin-top: ${`${1.25 * BODY_FONT_SIZE}px`};

    ${({ noHover, detectTouchDevice, theme }) => !noHover && `
        &:hover {
        ${(!detectTouchDevice && `
            background: ${theme.verascopeColor.orange100};
        `)}
        }
    `}

    ${({ theme }) => theme.mediaQuery.small} {
        font-size: ${`${CART_AND_CHECKOUT_FONT_SIZE_SMALL + 0.1}em`};
    }

    ${({ theme }) => theme.mediaQuery.extraSmall} {
        font-size: ${`${CART_AND_CHECKOUT_FONT_SIZE_SMALL + 0.1}em`};
    }
`;

export const ActionButtonLoadingContainer = styled.div`
    & img {
        width: ${`${1.25 * BODY_FONT_SIZE}px`};
    }
`;

export const ActionButtonLoadingMessageText = styled.h4`
    margin: 0;
    padding: 0;
    margin-top: ${`${0.3125 * BODY_FONT_SIZE}px`};
    font-family: ${FONT_TYPE.PRESS_START};
    font-weight: 500;
    font-size: ${`${CART_AND_CHECKOUT_FONT_SIZE_LARGE - 0.2}em`};
    color: ${({ theme }) => theme.color.white};
    text-transform: none;

    ${({ theme }) => theme.mediaQuery.small} {
        font-size: ${`${CART_AND_CHECKOUT_FONT_SIZE_SMALL - 0.2}em`};
    }

    ${({ theme }) => theme.mediaQuery.extraSmall} {
        font-size: ${`${CART_AND_CHECKOUT_FONT_SIZE_SMALL}em`};
    }
`;

export const CheckpointSubtitle = styled.h3`
    font-family: ${FONT_TYPE.PRESS_START};
    font-weight: 500;
    font-size: ${`${CART_AND_CHECKOUT_FONT_SIZE_LARGE - 0.2}em`};
    line-height: 1.8em;
    color: ${({ theme }) => theme.verascopeColor.purple300};
    text-transform: uppercase;
    margin: 0;
    margin-top: ${`${1.25 * BODY_FONT_SIZE}px`};
    margin-bottom: ${`${1.25 * BODY_FONT_SIZE}px`};

    ${({ theme }) => theme.mediaQuery.small} {
        font-size: ${`${CART_AND_CHECKOUT_FONT_SIZE_SMALL - 0.2}em`};
    }

    ${({ theme }) => theme.mediaQuery.extraSmall} {
        font-size: ${`${CART_AND_CHECKOUT_FONT_SIZE_SMALL - 0.2}em`};
        text-align: center;

        @media (hover: none) and (pointer: coarse) {
            font-size: 0.4em;
        }
    }
`;

export const PaymentWidgetContainer = styled.div`
    width: 100%;
`;

interface BillingAddressContainerProps {
    physicalItemsInCart: boolean,
}
export const BillingAddressContainer = styled.div<BillingAddressContainerProps>`
    padding: ${({ physicalItemsInCart }) => `${physicalItemsInCart ? 0.9375 * BODY_FONT_SIZE : 0}px`};
    padding-bottom: ${`${0.3125 * BODY_FONT_SIZE}px`};
    border: ${({ physicalItemsInCart, theme }) => (physicalItemsInCart
        ? `${0.1875 * BODY_FONT_SIZE}px solid ${theme.verascopeColor.purple100}`
        : 'none'
    )};
    border-top: none;
`;

export const ReviewShippingContainer = styled.div``;

export const ReviewShippingDetailContainer = styled.div`
    padding: ${`${0.9375 * BODY_FONT_SIZE}px`};
    margin-bottom: ${`${0.9375 * BODY_FONT_SIZE}px`};
    border: ${({ theme }) => `${0.1875 * BODY_FONT_SIZE}px solid ${theme.verascopeColor.purple100}`};

    ${({ theme }) => theme.mediaQuery.extraSmall} {
        padding: ${`${0.5 * BODY_FONT_SIZE}px`};
    }
`;

interface ReviewShippingDetailProps {
    noSeparator?: boolean,
}
export const ReviewShippingDetail = styled.div<ReviewShippingDetailProps>`
    display: flex;
    flex-direction: row;
    width: 100%;
    padding: ${`0px ${0.3125 * BODY_FONT_SIZE}px`};
    ${({ noSeparator, theme }) => !noSeparator && `
        padding-bottom: ${0.625 * BODY_FONT_SIZE}px;
        margin-bottom: ${0.9375 * BODY_FONT_SIZE}px;
        border-bottom: ${0.1875 * BODY_FONT_SIZE}px solid ${theme.verascopeColor.purple100};
    `}
`;

export const ReviewShippingDetailTitle = styled.h3`
    margin: 0;
    padding: 0;
    flex: ${`0 0 ${10.625 * BODY_FONT_SIZE}px`};
    width: ${`${10.625 * BODY_FONT_SIZE}px`};
    font-family: ${FONT_TYPE.PRESS_START};
    font-weight: 500;
    font-size: ${`${CART_AND_CHECKOUT_FONT_SIZE_LARGE - 0.2}em`};
    color: ${({ theme }) => theme.verascopeColor.purple300};
    text-transform: uppercase;

    ${({ theme }) => theme.mediaQuery.small} {
        font-size: ${`${CART_AND_CHECKOUT_FONT_SIZE_SMALL - 0.2}em`};
    }

    ${({ theme }) => theme.mediaQuery.extraSmall} {
        font-size: ${`${CART_AND_CHECKOUT_FONT_SIZE_SMALL - 0.2}em`};
        flex: ${`0 0 ${4.5 * BODY_FONT_SIZE}px`};
        width: ${`${4.5 * BODY_FONT_SIZE}px`};
        line-height: 1.8em;
    }
`;

export const ReviewShippingDetailBody = styled.div`
    position: relative;
    top: ${`-${0.25 * BODY_FONT_SIZE}px`};
    flex: 1 1 auto;

    ${({ theme }) => theme.mediaQuery.extraSmall} {
        top: 0px;
    }
`;

export const ReviewShippingDetailBodyLine = styled.h4`
    margin: 0;
    padding: 0;
    padding-right: ${`${3.4375 * BODY_FONT_SIZE}px`};
    width: 100%;
    font-family: ${FONT_TYPE.PRESS_START};
    font-weight: 500;
    font-size: ${`${CART_AND_CHECKOUT_FONT_SIZE_LARGE}em`};
    color: ${({ theme }) => theme.verascopeColor.purple100};
    text-transform: uppercase;
    line-height: 1.8em;
    word-break: break-all;

    ${({ theme }) => theme.mediaQuery.small} {
        font-size: ${`${CART_AND_CHECKOUT_FONT_SIZE_SMALL}em`};
    }

    ${({ theme }) => theme.mediaQuery.extraSmall} {
        font-size: ${`${CART_AND_CHECKOUT_FONT_SIZE_SMALL}em`};
    }
`;

interface ReviewShippingDetailBodyLinkProps {
    customCursorActive: boolean,
}
export const ReviewShippingDetailBodyLink = styled.a<ReviewShippingDetailBodyLinkProps>`
    font-family: ${FONT_TYPE.PRESS_START};
    font-weight: 500;
    font-size: ${`${CART_AND_CHECKOUT_FONT_SIZE_LARGE}em`};
    color: ${({ theme }) => theme.verascopeColor.orange200};
    text-transform: uppercase;
    line-height: 1.8em;
    text-decoration: none;
    cursor: ${({ customCursorActive }) => (customCursorActive ? 'none' : 'pointer')};

    &:hover {
        color: ${({ theme }) => theme.verascopeColor.orange200};
    }

    ${({ theme }) => theme.mediaQuery.small} {
        font-size: ${`${CART_AND_CHECKOUT_FONT_SIZE_SMALL}em`};
    }

    ${({ theme }) => theme.mediaQuery.extraSmall} {
        font-size: ${`${CART_AND_CHECKOUT_FONT_SIZE_SMALL}em`};
    }
`;

export const ReviewShippingDetailBodyLineHighlight = styled.span`
    color: ${({ theme }) => theme.verascopeColor.purple400};
    font-size: ${`${CART_AND_CHECKOUT_FONT_SIZE_LARGE - 0.02}em`};

    ${({ theme }) => theme.mediaQuery.small} {
        font-size: ${`${CART_AND_CHECKOUT_FONT_SIZE_SMALL - 0.02}em`};
    }

    ${({ theme }) => theme.mediaQuery.extraSmall} {
        font-size: ${`${CART_AND_CHECKOUT_FONT_SIZE_SMALL - 0.02}em`};
    }
`;

export const ReviewBookInviteIndexText = styled.span`
    color: ${({ theme }) => theme.verascopeColor.purple400};
`;

export const ReviewBookInviteBodyText = styled.span`
    color: ${({ theme }) => theme.verascopeColor.purple200};
    font-size: ${`${CART_AND_CHECKOUT_FONT_SIZE_LARGE - 0.02}em`};

    ${({ theme }) => theme.mediaQuery.small} {
        font-size: ${`${CART_AND_CHECKOUT_FONT_SIZE_SMALL - 0.02}em`};
    }

    ${({ theme }) => theme.mediaQuery.extraSmall} {
        font-size: ${`${CART_AND_CHECKOUT_FONT_SIZE_SMALL - 0.02}em`};
    }
`;

interface ReviewBookInviteEntryProps {
    hasTopGap?: boolean,
}
export const ReviewBookInviteEntry = styled.div<ReviewBookInviteEntryProps>`
    margin-top: ${({ hasTopGap }) => `${hasTopGap ? DEFAULT_FONT_SIZE : 0}px`};
`;

export const ReviewShippingDetailEditButtonContainer = styled.div`
    position: absolute;
    top: 0px;
    right: 0px;
    line-height: 0.7em;
    background: ${({ theme }) => theme.color.white};
`;

interface ReviewShippingDetailEditButtonProps {
    customCursorActive: boolean,
    detectTouchDevice: boolean,
}
export const ReviewShippingDetailEditButton = styled.button<ReviewShippingDetailEditButtonProps>`
    outline: none;
    border: none;
    background: none;
    cursor: ${({ customCursorActive }) => (customCursorActive ? 'none' : 'pointer')};
    padding: 0px;
    font-family: ${FONT_TYPE.PRESS_START};
    font-weight: 500;
    font-size: ${`${CART_AND_CHECKOUT_FONT_SIZE_LARGE - 0.1}em`};
    color: ${({ theme }) => theme.verascopeColor.orange200};
    text-transform: uppercase;
    background: ${({ theme }) => theme.color.white};

    &:hover {
        ${({ detectTouchDevice, theme }) => (!detectTouchDevice && `
            color: ${theme.verascopeColor.purple400};
        `)}
    }

    ${({ theme }) => theme.mediaQuery.small} {
        font-size: ${`${CART_AND_CHECKOUT_FONT_SIZE_SMALL - 0.1}em`};
    }

    ${({ theme }) => theme.mediaQuery.extraSmall} {
        font-size: ${`${CART_AND_CHECKOUT_FONT_SIZE_SMALL - 0.1}em`};
    }
`;

export const ReviewTransactionDetailContainer = styled.div`
    padding: ${`${0.9375 * BODY_FONT_SIZE}px`};
    border: ${({ theme }) => `${0.1875 * BODY_FONT_SIZE}px solid ${theme.verascopeColor.purple100}`};
`;

export const ReviewTransactionDetail = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    width: 100%;
    padding-left: ${`${0.3125 * BODY_FONT_SIZE}px`};
    margin-bottom: ${`${0.9375 * BODY_FONT_SIZE}px`};
`;

export const ReviewTransactionDetailTitle = styled.h3`
    margin: 0;
    padding: 0;
    flex: ${`0 0 ${10.625 * BODY_FONT_SIZE}px`};
    width: ${`${10.625 * BODY_FONT_SIZE}px`};
    font-family: ${FONT_TYPE.PRESS_START};
    font-weight: 500;
    font-size: ${`${CART_AND_CHECKOUT_FONT_SIZE_LARGE - 0.2}em`};
    color: ${({ theme }) => theme.verascopeColor.purple100};
    text-transform: uppercase;

    ${({ theme }) => theme.mediaQuery.small} {
        font-size: ${`${CART_AND_CHECKOUT_FONT_SIZE_SMALL - 0.2}em`};
    }

    ${({ theme }) => theme.mediaQuery.extraSmall} {
        font-size: ${`${CART_AND_CHECKOUT_FONT_SIZE_SMALL - 0.2}em`};
        flex: ${`0 0 ${6.25 * BODY_FONT_SIZE}px`};
        width: ${`${6.25 * BODY_FONT_SIZE}px`};
        line-height: 1.8em;
    }
`;

export const ReviewTransactionDetailBody = styled.h4`
    margin: 0;
    padding: 0;
    flex: 1 1 auto;
    font-family: ${FONT_TYPE.PRESS_START};
    font-weight: 500;
    font-size: ${`${CART_AND_CHECKOUT_FONT_SIZE_LARGE}em`};
    color: ${({ theme }) => theme.verascopeColor.purple400};
    text-transform: uppercase;
    text-align: right;

    ${({ theme }) => theme.mediaQuery.small} {
        font-size: ${`${CART_AND_CHECKOUT_FONT_SIZE_SMALL}em`};
    }

    ${({ theme }) => theme.mediaQuery.extraSmall} {
        font-size: ${`${CART_AND_CHECKOUT_FONT_SIZE_SMALL}em`};
    }
`;

export const ReviewTransactionTotal = styled.div`
    display: flex;
    flex-direction: row;
    width: 100%;
    padding-top: ${`${1.25 * BODY_FONT_SIZE}px`};
    border-top: ${({ theme }) => `${0.1875 * BODY_FONT_SIZE}px solid ${theme.verascopeColor.purple100}`};
`;

export const ReviewTransactionTotalTitle = styled.h2`
    margin: 0;
    padding: 0;
    flex: ${`0 0 ${18.75 * BODY_FONT_SIZE}px`};
    width: ${`${18.75 * BODY_FONT_SIZE}px`};
    font-family: ${FONT_TYPE.PRESS_START};
    font-weight: 500;
    font-size: ${`${CART_AND_CHECKOUT_FONT_SIZE_LARGE}em`};
    color: ${({ theme }) => theme.verascopeColor.purple100};
    text-transform: uppercase;

    ${({ theme }) => theme.mediaQuery.small} {
        font-size: ${`${CART_AND_CHECKOUT_FONT_SIZE_SMALL}em`};
    }

    ${({ theme }) => theme.mediaQuery.extraSmall} {
        font-size: ${`${CART_AND_CHECKOUT_FONT_SIZE_SMALL}em`};
        flex: ${`0 0 ${6.25 * BODY_FONT_SIZE}px`};
        width: ${`${6.25 * BODY_FONT_SIZE}px`};
    }
`;

export const ReviewTransactionTotalBody = styled.h4`
    margin: 0;
    padding: 0;
    flex: 1 1 auto;
    font-family: ${FONT_TYPE.PRESS_START};
    font-weight: 500;
    font-size: ${`${CART_AND_CHECKOUT_FONT_SIZE_LARGE}em`};
    color: ${({ theme }) => theme.verascopeColor.purple400};
    text-transform: uppercase;
    text-align: right;

    ${({ theme }) => theme.mediaQuery.small} {
        font-size: ${`${CART_AND_CHECKOUT_FONT_SIZE_SMALL}em`};
    }

    ${({ theme }) => theme.mediaQuery.extraSmall} {
        font-size: ${`${CART_AND_CHECKOUT_FONT_SIZE_SMALL}em`};
    }
`;

interface BillingContainerProps {
    visible: boolean,
}
export const BillingContainer = styled.div<BillingContainerProps>`
    height: ${({ visible }) => (visible ? 'auto' : '0px')};
    overflow: ${({ visible }) => (visible ? 'visible' : 'hidden')};
`;

interface ReviewContainerProps {
    visible: boolean,
}
export const ReviewContainer = styled.div<ReviewContainerProps>`
    height: ${({ visible }) => (visible ? 'auto' : '0px')};
    overflow: ${({ visible }) => (visible ? 'visible' : 'hidden')};
`;

interface MailingListContainerProps {
    largeMarginTop: boolean,
}
export const MailingListContainer = styled.div<MailingListContainerProps>`
    margin-top: ${({ largeMarginTop }) => `${largeMarginTop ? 1.875 * BODY_FONT_SIZE : 0.625 * BODY_FONT_SIZE}px`};
    margin-bottom: ${`${1.875 * BODY_FONT_SIZE}px`};

    ${({ theme }) => theme.mediaQuery.extraSmall} {
        display: flex;
        flex-direction: row;
        justify-content: center;
    }
`;

export const SavePaymentMethodContainer = styled.div`
    margin-top: ${`${0.625 * BODY_FONT_SIZE}px`};
    
    ${({ theme }) => theme.mediaQuery.extraSmall} {
        display: flex;
        flex-direction: row;
        justify-content: center;
    }
`;

interface PaymentElementContainerProps {
    visible: boolean,
}
export const PaymentElementContainer = styled.div<PaymentElementContainerProps>`
    width: ${`calc(100% - ${0.3125 * BODY_FONT_SIZE}px)`};
    margin: ${`${1.25 * BODY_FONT_SIZE}px 0px`};
    height: ${({ visible }) => (visible ? 'auto' : '0px')};
    overflow: ${({ visible }) => (visible ? 'visible' : 'hidden')};
`;

export const PaymentTrustText = styled.p`
    margin: 0;
    margin-top: ${`${0.625 * BODY_FONT_SIZE}px`};
    font-family: ${FONT_TYPE.SOURCE_SANS_3};
    font-size: ${`${CART_AND_CHECKOUT_FONT_SIZE_LARGE - 0.1}em`};
    line-height: 1.6em;
    color: ${({ theme }) => theme.verascopeColor.purple300};

    ${({ theme }) => theme.mediaQuery.small} {
        font-size: ${`${CART_AND_CHECKOUT_FONT_SIZE_SMALL - 0.1}em`};
    }

    ${({ theme }) => theme.mediaQuery.extraSmall} {
        font-size: ${`${CART_AND_CHECKOUT_FONT_SIZE_SMALL - 0.1}em`};
        text-align: center;
    }
`;

export const CheckoutPolicyText = styled.p`
    margin: 0;
    margin-top: ${`${0.75 * BODY_FONT_SIZE}px`};
    background: ${({ theme }) => theme.color.white};
    font-family: ${FONT_TYPE.SOURCE_SANS_3};
    font-size: ${`${CART_AND_CHECKOUT_FONT_SIZE_LARGE - 0.1}em`};
    line-height: 1.6em;
    color: ${({ theme }) => theme.verascopeColor.purple300};

    ${({ theme }) => theme.mediaQuery.small} {
        font-size: ${`${CART_AND_CHECKOUT_FONT_SIZE_SMALL - 0.1}em`};
    }

    ${({ theme }) => theme.mediaQuery.extraSmall} {
        font-size: ${`${CART_AND_CHECKOUT_FONT_SIZE_SMALL - 0.1}em`};
        text-align: center;
    }
`;

interface CheckoutPolicyLinkProps {
    customCursorActive: boolean,
}
export const CheckoutPolicyLink = styled.a<CheckoutPolicyLinkProps>`
    background: ${({ theme }) => theme.color.white};
    color: ${({ theme }) => theme.verascopeColor.orange200};
    text-decoration: none;
    cursor: ${({ customCursorActive }) => (customCursorActive ? 'none' : 'pointer')};

    &:hover {
        color: ${({ theme }) => theme.verascopeColor.orange200};
    }
`;

export const BookInviteSectionContainer = styled.div`
    width: 100%;
    margin-top: ${`${1.25 * BODY_FONT_SIZE}px`};
    margin-bottom: ${`${0.625 * BODY_FONT_SIZE}px`};
    border-bottom: ${({ theme }) => `${0.1875 * BODY_FONT_SIZE}px solid ${theme.verascopeColor.purple100}`};
`;

interface BookInviteSectionHeaderButtonProps {
    customCursorActive: boolean,
}
export const BookInviteSectionHeaderButton = styled.button<BookInviteSectionHeaderButtonProps>`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    border: none;
    background: ${({ theme }) => theme.color.white};
    font-size: inherit;
    font-family: inherit;
    line-height: inherit;
    cursor: ${({ customCursorActive }) => (customCursorActive ? 'none' : 'pointer')};
    text-align: left;
    padding: ${`${0.625 * BODY_FONT_SIZE}px 0px`};
    touch-action: pan-y;

    @media (hover: hover) and (pointer: fine) {
        &:hover > div:first-child h4 {
            color: ${({ theme }) => theme.verascopeColor.orange200};
        }

        &:hover > div:last-child {
            background: ${({ theme }) => theme.verascopeColor.yellow200};
        }

        &:hover > div:last-child div {
            border-top: ${({ theme }) => `${0.3125 * BODY_FONT_SIZE}px solid ${theme.verascopeColor.purple100}`};
            background: ${({ theme }) => theme.verascopeColor.yellow200};
        }
    }
`;

export const BookInviteSectionHeaderTextContainer = styled.div`
    flex: 1 1 auto;
`;

export const BookInviteSectionHeaderTitle = styled.h4`
    margin: 0;
    font-family: ${FONT_TYPE.PRESS_START};
    font-weight: 500;
    font-size: ${`${CART_AND_CHECKOUT_FONT_SIZE_LARGE}em`};
    color: ${({ theme }) => theme.verascopeColor.purple100};
    text-transform: uppercase;
    line-height: 2em;

    ${({ theme }) => theme.mediaQuery.small} {
        font-size: ${`${CART_AND_CHECKOUT_FONT_SIZE_SMALL}em`};
        line-height: 1.8em;
    }
`;

export const BookInviteSectionHeaderSummary = styled.p`
    margin: 0;
    margin-top: ${`${1.25 * BODY_FONT_SIZE}px`};
    margin-bottom: ${`${1.25 * BODY_FONT_SIZE}px`};
    font-family: ${FONT_TYPE.PRESS_START};
    font-weight: 500;
    font-size: ${`${CART_AND_CHECKOUT_FONT_SIZE_LARGE - 0.2}em`};
    color: ${({ theme }) => theme.verascopeColor.purple300};
    line-height: 1.8em;
    text-transform: uppercase;

    ${({ theme }) => theme.mediaQuery.small} {
        font-size: ${`${CART_AND_CHECKOUT_FONT_SIZE_SMALL - 0.2}em`};
    }

    ${({ theme }) => theme.mediaQuery.extraSmall} {
        font-size: ${`${(CART_AND_CHECKOUT_FONT_SIZE_SMALL - 0.2) * 1.125}em`};
        text-align: center;
    }
`;

export const BookInviteSectionHeaderSummaryValue = styled.span`
    color: ${({ theme }) => theme.verascopeColor.orange200};
`;

export const BookInviteSectionToggleButton = styled.div`
    width: 0;
    height: 0;
    border-left: ${`${0.3125 * BODY_FONT_SIZE}px solid transparent`};
    border-right: ${`${0.3125 * BODY_FONT_SIZE}px solid transparent`};
    border-top: ${({ theme }) => `${0.3125 * BODY_FONT_SIZE}px solid ${theme.verascopeColor.purple100}`};
    background: ${({ theme }) => theme.color.white};
`;

interface BookInviteSectionToggleButtonContainerProps {
    expanded: boolean,
    detectTouchDevice: boolean,
    customCursorActive: boolean,
}
export const BookInviteSectionToggleButtonContainer = styled.div<BookInviteSectionToggleButtonContainerProps>`
    display: flex;
    align-items: center;
    justify-content: center;
    height: ${`${1.875 * BODY_FONT_SIZE}px`};
    width: ${`${1.875 * BODY_FONT_SIZE}px`};
    border-radius: ${`${0.9375 * BODY_FONT_SIZE}px`};
    padding: ${`${0.625 * BODY_FONT_SIZE}px`};
    margin-left: ${`${0.625 * BODY_FONT_SIZE}px`};
    background: ${({ theme }) => theme.color.white};
    transform: ${({ expanded }) => `rotate(${expanded ? 180 : 0}deg)`};
    cursor: ${({ customCursorActive }) => (customCursorActive ? 'none' : 'pointer')};

    &:hover {
        ${({ detectTouchDevice, theme }) => (!detectTouchDevice && `
            background: ${theme.verascopeColor.yellow200};
        `)}
    }

    &:hover ${BookInviteSectionToggleButton} {
        ${({ detectTouchDevice, theme }) => (!detectTouchDevice && `
            border-top: ${0.3125 * BODY_FONT_SIZE}px solid ${theme.verascopeColor.purple100};
            background: ${theme.verascopeColor.yellow200};
        `)}
    }
`;

export const BookInviteSectionBody = styled.div`
    padding: ${`${0.9375 * BODY_FONT_SIZE}px`};
    border-top: ${({ theme }) => `${0.1875 * BODY_FONT_SIZE}px solid ${theme.verascopeColor.purple100}`};
`;

export const BookInviteSelfAssignmentRow = styled.div`
    ${({ theme }) => theme.mediaQuery.extraSmall} {
        display: flex;
        justify-content: center;
        width: 100%;
    }
`;

export const BookInviteActionRow = styled.div`
    display: flex;
    width: 100%;
    gap: ${`${0.625 * BODY_FONT_SIZE}px`};
    margin-top: ${`${0.625 * BODY_FONT_SIZE}px`};
    margin-bottom: ${`${0.625 * BODY_FONT_SIZE}px`};

    ${({ theme }) => theme.mediaQuery.extraSmall} {
        flex-direction: column;
    }
`;

interface BookInviteToggleActionButtonProps {
    active: boolean,
    customCursorActive: boolean,
}
export const BookInviteToggleActionButton = styled.button<BookInviteToggleActionButtonProps>`
    border: ${({ active, theme }) => `${0.1875 * BODY_FONT_SIZE}px solid ${active
        ? 'transparent'
        : theme.verascopeColor.purple100
    }`};
    background: ${({ active, theme }) => (active
        ? theme.verascopeColor.orange200
        : theme.color.white
    )};
    color: ${({ active, theme }) => (active
        ? theme.color.white
        : theme.verascopeColor.purple100
    )};
    cursor: ${({ customCursorActive }) => (customCursorActive ? 'none' : 'pointer')};
    font-family: ${FONT_TYPE.PRESS_START};
    font-weight: 500;
    font-size: ${`${CART_AND_CHECKOUT_FONT_SIZE_LARGE - 0.2}em`};
    text-transform: uppercase;
    padding: ${`${0.625 * BODY_FONT_SIZE}px`};
    width: 100%;

    @media (hover: hover) and (pointer: fine) {
        &:hover {
            background: ${({ active, theme }) => (active ? theme.verascopeColor.orange100 : theme.color.white)};
            color: ${({ active, theme }) => (active ? theme.color.white : theme.verascopeColor.purple400)};
            border-color: ${({ active, theme }) => (active ? 'transparent' : theme.verascopeColor.purple400)};
        }
    }

    ${({ theme }) => theme.mediaQuery.small} {
        font-size: ${`${CART_AND_CHECKOUT_FONT_SIZE_SMALL - 0.2}em`};
    }
`;

export const BookInviteSubtext = styled.p`
    margin: 0;
    margin-top: ${`${0.5 * BODY_FONT_SIZE}px`};
    margin-bottom: ${`${0.625 * BODY_FONT_SIZE}px`};
    background: ${({ theme }) => theme.color.white};
    font-family: ${FONT_TYPE.SOURCE_SANS_3};
    font-size: ${`${CART_AND_CHECKOUT_FONT_SIZE_LARGE - 0.1}em`};
    color: ${({ theme }) => theme.verascopeColor.purple300};
    line-height: 1.5em;

    ${({ theme }) => theme.mediaQuery.small} {
        font-size: ${`${CART_AND_CHECKOUT_FONT_SIZE_SMALL - 0.1}em`};
    }

    ${({ theme }) => theme.mediaQuery.extraSmall} {
        text-align: center;
    }
`;

interface BookInviteSubtextLinkProps {
    customCursorActive: boolean,
}
export const BookInviteSubtextLink = styled.button<BookInviteSubtextLinkProps>`
    border: none;
    background: ${({ theme }) => theme.color.white};
    padding: 0;
    margin: 0;
    font-family: inherit;
    font-size: inherit;
    line-height: inherit;
    color: ${({ theme }) => theme.verascopeColor.orange200};
    text-decoration: none;
    cursor: ${({ customCursorActive }) => (customCursorActive ? 'none' : 'pointer')};

    &:hover {
        color: ${({ theme }) => theme.verascopeColor.orange200};
    }
`;

export const BookInviteRecipientInputRow = styled.div`
    display: flex;
    align-items: center;
    width: 100%;
`;

interface BookInviteRecipientInputContainerProps {
    addButtonVisible: boolean,
}
export const BookInviteRecipientInputContainer = styled.div<BookInviteRecipientInputContainerProps>`
    width: ${({ addButtonVisible }) => (addButtonVisible
        ? `calc(100% - ${5 * BODY_FONT_SIZE}px)`
        : '100%')};
`;

interface BookInviteRecipientAddButtonProps {
    customCursorActive: boolean,
}
export const BookInviteRecipientAddButton = styled.button<BookInviteRecipientAddButtonProps>`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    outline: none;
    border: none;
    cursor: ${({ customCursorActive }) => (customCursorActive ? 'none' : 'pointer')};
    padding: 0px;
    width: ${`${4.375 * BODY_FONT_SIZE}px`};
    height: ${`${2.5 * BODY_FONT_SIZE}px`};
    margin-left: ${`${0.625 * BODY_FONT_SIZE}px`};
    font-family: ${FONT_TYPE.PRESS_START};
    font-weight: 500;
    font-size: ${`${CART_AND_CHECKOUT_FONT_SIZE_LARGE - 0.1}em`};
    color: ${({ theme }) => theme.color.white};
    text-transform: uppercase;
    background: ${({ theme }) => theme.verascopeColor.purple100};

    &:hover {
        background: ${({ theme }) => theme.verascopeColor.orange200};
    }

    ${({ theme }) => theme.mediaQuery.small} {
        font-size: ${`${CART_AND_CHECKOUT_FONT_SIZE_SMALL - 0.1}em`};
    }
`;

interface BookInviteValidationTextProps {
    hasError: boolean,
}
export const BookInviteValidationText = styled.p<BookInviteValidationTextProps>`
    margin: 0;
    margin-top: ${`${0.5 * BODY_FONT_SIZE}px`};
    font-family: ${FONT_TYPE.SOURCE_SANS_3};
    font-size: ${`${CART_AND_CHECKOUT_FONT_SIZE_LARGE - 0.1}em`};
    color: ${({ hasError, theme }) => (hasError
        ? theme.verascopeColor.red200
        : theme.verascopeColor.purple400
    )};
    line-height: 1.5em;

    ${({ theme }) => theme.mediaQuery.small} {
        font-size: ${`${CART_AND_CHECKOUT_FONT_SIZE_SMALL - 0.1}em`};
    }

    ${({ theme }) => theme.mediaQuery.extraSmall} {
        text-align: center;
    }
`;

export const BookInviteAssignedSummaryText = styled.p`
    margin: 0;
    margin-top: ${`${0.5 * BODY_FONT_SIZE}px`};
    font-family: ${FONT_TYPE.PRESS_START};
    font-weight: 500;
    font-size: ${`${CART_AND_CHECKOUT_FONT_SIZE_LARGE - 0.2}em`};
    line-height: 1.8em;
    color: ${({ theme }) => theme.verascopeColor.purple300};
    text-transform: uppercase;

    ${({ theme }) => theme.mediaQuery.small} {
        font-size: ${`${CART_AND_CHECKOUT_FONT_SIZE_SMALL - 0.2}em`};
    }

    ${({ theme }) => theme.mediaQuery.extraSmall} {
        text-align: center;
    }
`;

export const BookInviteChipList = styled.div`
    display: flex;
    flex-direction: column;
    margin-top: ${`${0.625 * BODY_FONT_SIZE}px`};
`;

interface BookInviteCardProps {
    hasError: boolean,
}
export const BookInviteCard = styled.div<BookInviteCardProps>`
    display: flex;
    flex-direction: row;
    align-items: stretch;
    border: ${({ hasError, theme }) => `${0.1875 * BODY_FONT_SIZE}px solid ${hasError
        ? theme.verascopeColor.red200
        : theme.verascopeColor.purple200
    }`};
    padding: ${`${0.46875 * BODY_FONT_SIZE}px`};
    margin-bottom: ${`${0.625 * BODY_FONT_SIZE}px`};

    ${({ theme }) => theme.mediaQuery.extraSmall} {
        flex-direction: column;
    }
`;

export const BookInviteIndex = styled.div`
    display: flex;
    align-items: flex-start;
    justify-content: flex-start;
    min-width: ${`${5.5 * BODY_FONT_SIZE}px`};
    padding-right: ${`${0.625 * BODY_FONT_SIZE}px`};

    ${({ theme }) => theme.mediaQuery.extraSmall} {
        min-width: 0px;
        padding-right: 0px;
        margin-bottom: ${`${0.3125 * BODY_FONT_SIZE}px`};
        width: 100%;
        align-items: center;
        justify-content: center;
    }
`;

export const BookInviteIndexText = styled.h6`
    margin: 0;
    font-family: ${FONT_TYPE.PRESS_START};
    font-weight: 500;
    font-size: ${`${CART_AND_CHECKOUT_FONT_SIZE_LARGE - 0.3}em`};
    color: ${({ theme }) => theme.verascopeColor.purple100};
    text-transform: uppercase;
    line-height: 1.6em;

    ${({ theme }) => theme.mediaQuery.small} {
        font-size: ${`${CART_AND_CHECKOUT_FONT_SIZE_SMALL - 0.3}em`};
    }

    ${({ theme }) => theme.mediaQuery.extraSmall} {
        text-align: center;
    }
`;

export const BookInviteBody = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
`;

export const BookInviteActionsRow = styled.div`
    display: flex;
    justify-content: flex-end;
    align-items: center;
    gap: ${`${0.375 * BODY_FONT_SIZE}px`};
    margin-top: ${`${0.5 * BODY_FONT_SIZE}px`};
`;

export const BookInviteActionButtonContainer = styled.div`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: ${({ theme }) => theme.color.white};
    border-radius: 50%;
`;

interface BookInviteActionButtonProps {
    customCursorActive: boolean,
    $active: boolean,
}
export const BookInviteActionButton = styled.button<BookInviteActionButtonProps>`
    border: ${({ theme }) => `${0.1875 * BODY_FONT_SIZE}px solid ${theme.verascopeColor.purple100}`};
    background: ${({ theme, $active }) => (
        $active ? theme.verascopeColor.purple100 : theme.color.neutral200
    )};
    color: ${({ theme, $active }) => (
        $active ? theme.color.white : theme.verascopeColor.purple100
    )};
    cursor: ${({ customCursorActive }) => (customCursorActive ? 'none' : 'pointer')};
    font-family: ${FONT_TYPE.PRESS_START};
    font-weight: 500;
    font-size: ${`${CART_AND_CHECKOUT_FONT_SIZE_LARGE - 0.3}em`};
    text-transform: uppercase;
    padding: ${`${0.4375 * BODY_FONT_SIZE}px ${0.625 * BODY_FONT_SIZE}px`};

    @media (hover: hover) and (pointer: fine) {
        &:hover {
            background: ${({ theme, $active }) => ($active ? theme.verascopeColor.purple100 : theme.color.neutral200)};
            border-color: ${({ theme, $active }) => ($active ? theme.verascopeColor.purple100 : theme.verascopeColor.purple400)};
            color: ${({ theme, $active }) => ($active ? theme.color.white : theme.verascopeColor.purple400)};
        }
    }

    ${({ theme }) => theme.mediaQuery.small} {
        font-size: ${`${CART_AND_CHECKOUT_FONT_SIZE_SMALL - 0.3}em`};
    }
`;

export const BookInviteEmailPreviewContainer = styled.div`
    border: ${({ theme }) => `${0.1875 * BODY_FONT_SIZE}px solid ${theme.verascopeColor.purple100}`};
    border-radius: 0px;
    background: ${({ theme }) => theme.color.white};
    padding: ${`${0.35 * BODY_FONT_SIZE}px ${0.45 * BODY_FONT_SIZE}px`};
    margin-top: ${`${0.35 * BODY_FONT_SIZE}px`};
`;

export const BookInviteEmailPreviewHeader = styled.p`
    margin: 0;
    font-family: ${FONT_TYPE.PRESS_START};
    font-size: ${`${CART_AND_CHECKOUT_FONT_SIZE_LARGE - 0.35}em`};
    color: ${({ theme }) => theme.verascopeColor.purple100};
    text-transform: uppercase;
`;

export const BookInviteEmailPreviewToLine = styled.p`
    margin: 0;
    margin-top: ${`${0.5 * BODY_FONT_SIZE}px`};
    margin-bottom: ${`${0.2 * BODY_FONT_SIZE}px`};
    font-family: ${FONT_TYPE.PRESS_START};
    font-size: ${`${CART_AND_CHECKOUT_FONT_SIZE_LARGE - 0.25}em`};
    font-weight: 500;
    color: ${({ theme }) => theme.verascopeColor.purple100};
    text-transform: uppercase;
`;

export const BookInviteEmailPreviewSubject = styled.p`
    margin: 0;
    margin-top: ${`${0.6 * BODY_FONT_SIZE}px`};
    margin-bottom: ${`${0.45 * BODY_FONT_SIZE}px`};
    font-family: ${FONT_TYPE.PRESS_START};
    font-size: ${`${CART_AND_CHECKOUT_FONT_SIZE_LARGE - 0.15}em`};
    font-weight: 500;
    color: ${({ theme }) => theme.verascopeColor.purple400};
    text-transform: uppercase;
    line-height: 2em;
`;

export const BookInviteEmailPreviewBody = styled.pre`
    margin: 0;
    margin-top: ${`${0.8 * BODY_FONT_SIZE}px`};
    max-width: 60ch;
    white-space: pre-wrap;
    word-break: break-word;
    font-family: ${FONT_TYPE.SOURCE_SANS_3};
    font-size: ${`${CART_AND_CHECKOUT_FONT_SIZE_LARGE - 0.15}em`};
    font-weight: 500;
    line-height: 1.45em;
    color: ${({ theme }) => theme.verascopeColor.purple300};
    text-transform: none;
`;

export const BookInviteEmailPreviewLoadingContainer = styled.div`
    margin-top: ${`${0.8 * BODY_FONT_SIZE}px`};
    display: flex;
    justify-content: center;
`;

export const BookInvitePreviewSpinner = styled(ReactSVG)`
    display: inline-flex;

    & > div {
        display: flex;
        align-items: center;
        justify-content: center;
    }

    & svg {
        width: ${`${1.25 * BODY_FONT_SIZE}px`};
        height: ${`${1.25 * BODY_FONT_SIZE}px`};
        animation: rotate 2s linear infinite;
    }

    & path {
        fill: ${({ theme }) => theme.verascopeColor.purple200};
    }
`;

export const BookInviteSecondaryInputScale = styled.div`
    font-size: 0.82em;

    ${({ theme }) => theme.mediaQuery.extraSmall} {
        font-size: 0.95em;
    }
`;

export const BookInviteRecipientNameInputScale = styled(BookInviteSecondaryInputScale)`
    ${({ theme }) => theme.mediaQuery.extraSmall} {
        margin-bottom: ${`${0.3125 * BODY_FONT_SIZE}px`};
    }
`;

interface BookInviteChipRowProps {
    hasError: boolean,
}
export const BookInviteChipRow = styled.div<BookInviteChipRowProps>`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    border: ${({ hasError, theme }) => `${0.1875 * BODY_FONT_SIZE}px solid ${hasError
        ? theme.verascopeColor.red200
        : theme.verascopeColor.purple400
    }`};
    background: ${({ hasError, theme }) => (hasError
        ? theme.verascopeColor.red200
        : theme.verascopeColor.purple400
    )};
    padding: ${`${0.4375 * BODY_FONT_SIZE}px ${0.625 * BODY_FONT_SIZE}px`};
    margin-bottom: ${`${0.5 * BODY_FONT_SIZE}px`};
`;

export const BookInviteChipText = styled.h5`
    margin: 0;
    font-family: ${FONT_TYPE.PRESS_START};
    font-weight: 500;
    font-size: ${`${CART_AND_CHECKOUT_FONT_SIZE_LARGE - 0.25}em`};
    color: ${({ theme }) => theme.color.white};
    text-transform: uppercase;
    line-height: 1.8em;
    word-break: break-all;

    ${({ theme }) => theme.mediaQuery.small} {
        font-size: ${`${CART_AND_CHECKOUT_FONT_SIZE_SMALL - 0.25}em`};
    }
`;

interface BookInviteChipRemoveButtonProps {
    customCursorActive: boolean,
    hasError: boolean,
}
export const BookInviteChipRemoveButton = styled.button<BookInviteChipRemoveButtonProps>`
    outline: none;
    border: none;
    cursor: ${({ customCursorActive }) => (customCursorActive ? 'none' : 'pointer')};
    padding: ${`${0.4375 * BODY_FONT_SIZE}px`};
    margin-left: ${`${0.625 * BODY_FONT_SIZE}px`};
    width: ${`${1.875 * BODY_FONT_SIZE}px`};
    height: ${`${1.875 * BODY_FONT_SIZE}px`};
    border-radius: ${`${0.9375 * BODY_FONT_SIZE}px`};
    background: ${({ hasError, theme }) => (hasError
        ? theme.verascopeColor.red200
        : theme.verascopeColor.purple400
    )};
    flex-shrink: 0;

    & div {
        display: flex;
        align-items: center;
        justify-content: center;
        height: 100%;
        width: 100%;
    }

    & svg {
        width: 100%;
        height: 100%;
        overflow: visible;
    }

    & path, & rect {
        fill: ${({ theme }) => theme.color.white};
    }

    &:hover {
        background: ${({ theme }) => theme.verascopeColor.orange200};
    }
`;
